// Work waiting to be booked (office-booking prototype): estimates sold for later, membership
// visits that are due, and leads (requests not booked yet). Each needs a new job; the office books it.

import { normalizePhone } from '@flowology/kernel';

export interface SoldEstimate {
  estimateId: string;
  number: string;
  jobId: string;
  jobNumber: string;
  customerId: string;
  customerName: string;
  optionTitle: string;
  totalCents: number;
  soldAt: string;
  signedOnline: boolean;
  held: boolean;
  holdReason: string | null;
  cancelRequested: boolean;
}

export interface DueVisit {
  recurringServiceId: string;
  membershipId: string;
  customerId: string;
  locationId: string;
  jobTypeId: string;
  dueOn: string;
  customerName: string;
  address: string;
  membershipName: string;
  jobTypeName: string;
  businessUnitId: string | null;
  endsOn: string;
  visitsLeft: number;
}

/** What a sold estimate's card says, whether it can be booked now, and what the office clears first. */
export function soldState(e: SoldEstimate): {
  chip: string;
  tone: 'ok' | 'warn' | '';
  bookable: boolean;
  why: string | null;
  /** estimates.clearHold's `clear`, once someone has spoken with the customer (QA-576). */
  clear: 'hold' | 'cancel_request' | null;
} {
  if (e.cancelRequested)
    return {
      chip: 'Customer asked to cancel',
      tone: 'warn',
      bookable: false,
      why: 'Call the customer. If they still want the work, clear their request, then book it.',
      clear: 'cancel_request',
    };
  if (e.held)
    return {
      chip: 'Held',
      tone: 'warn',
      bookable: false,
      why: e.holdReason ?? 'The signature is being checked; book it once it is cleared.',
      clear: 'hold',
    };
  return {
    chip: e.signedOnline ? 'Signed online' : 'Signed on the iPad',
    tone: 'ok',
    bookable: true,
    why: null,
    clear: null,
  };
}

/** Visits due by a date: the next 30 days from today, so the office books ahead. */
export function dueHorizon(today: string, days = 30): string {
  const d = new Date(`${today}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** "Overdue", "Due today" or "Due in 5 days" for a company-local date. */
export function dueWords(dueOn: string, today: string): { text: string; tone: 'warn' | '' } {
  const days = Math.round((Date.parse(`${dueOn}T12:00:00Z`) - Date.parse(`${today}T12:00:00Z`)) / 86_400_000);
  if (days < 0) return { text: `Overdue by ${-days} day${days === -1 ? '' : 's'}`, tone: 'warn' };
  if (days === 0) return { text: 'Due today', tone: 'warn' };
  return { text: `Due in ${days} day${days === 1 ? '' : 's'}`, tone: '' };
}

// ── leads (leads.list, leads.create, leads.close) ──

/** Who works leads: whoever holds leads.manage (the office's four who book; the same people who open To book). */
export const LEAD_PERMS = ['leads.manage'];

export type LeadStatus = 'open' | 'booked' | 'closed';
export type LeadSource = 'web_form' | 'email' | 'call' | 'referral' | 'other';

export interface Lead {
  id: string;
  source: string;
  status: LeadStatus;
  name: string | null;
  phone: string | null;
  email: string | null;
  summary: string;
  customerId: string | null;
  jobId: string | null;
  createdAt: string;
}

export const LEAD_SOURCES: { value: LeadSource; label: string }[] = [
  { value: 'call', label: 'A call' },
  { value: 'web_form', label: 'Web form' },
  { value: 'email', label: 'Email' },
  { value: 'referral', label: 'Referral' },
  { value: 'other', label: 'Something else' },
];

export const sourceLabel = (s: string) => LEAD_SOURCES.find((x) => x.value === s)?.label ?? s;

/** Reasons a lead is closed without a job; "Something else" asks for the words. */
export const LEAD_CLOSE_REASONS = [
  'Not a job we do',
  'Out of our area',
  'Went with someone else',
  'Price too high',
  'Couldn’t reach them',
  'Duplicate',
];

export interface LeadForm {
  source: LeadSource;
  summary: string;
  name: string;
  phone: string;
  email: string;
}

export const emptyLeadForm = (): LeadForm => ({
  source: 'call',
  summary: '',
  name: '',
  phone: '',
  email: '',
});

/** leads.create's input, or the one thing to fix first. The server's own rule is the last word. */
export function leadInput(f: LeadForm): { input: Record<string, unknown> } | { error: string } {
  const summary = f.summary.trim();
  if (!summary) return { error: 'Say what they want.' };
  const phone = f.phone.trim() ? normalizePhone(f.phone) : undefined;
  if (phone === null)
    return { error: 'That phone number doesn’t look right. Type it with its area code, like 604-555-0198.' };
  const email = f.email.trim();
  if (!phone && !email)
    return { error: 'A lead needs a way to reach them: a phone, an email or a customer.' };
  const name = f.name.trim();
  return {
    input: {
      source: f.source,
      summary,
      ...(name ? { name } : {}),
      ...(phone ? { phone } : {}),
      ...(email ? { email } : {}),
    },
  };
}

export function closeReason(choice: string, note: string): string {
  const n = note.trim();
  return choice ? (n ? `${choice}: ${n}` : choice) : n;
}

// ── Asked to move (F-08): customers' requests from their visit links ──

export type VisitPart = 'morning' | 'afternoon' | 'any';

/** A customer's open request about a booked visit (visitRequests.list). */
export interface VisitRequest {
  id: string;
  kind: 'change' | 'cancel';
  times: { date: string; part: VisitPart; text: string }[];
  note: string | null;
  createdAt: string;
  customer: { id: string; name: string; phone: string | null };
  visit: {
    appointmentId: string;
    jobId: string;
    jobNumber: string;
    status: string;
    startsAt: string;
    endsAt: string;
    whenText: string;
    summary: string;
    /** The job's only visit still open: it can't be cancelled on its own, only with the job (QA-532). */
    onlyVisit: boolean;
  };
}

/** "Asked Oct 17, 2026, 9:15 a.m.": one full stop, whether or not the time ends in one (QA-534). */
export function askedText(createdAt: string, timeZone: string): string {
  const at = new Intl.DateTimeFormat('en-CA', { timeZone, dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(createdAt),
  );
  return `Asked ${at}${at.endsWith('.') ? '' : '.'}`;
}

/**
 * The live events that change the list: a request sent or dealt with, a visit moved or cancelled, or
 * its job cancelled (from the job sheet: the card then says to close the request, QA-533).
 */
export const VISIT_REQUEST_EVENTS =
  /^(visit\.(change|cancel)_requested|visit_request\.resolved|appointment\.(rescheduled|cancelled)|job\.cancelled)$/;

/** The arrival window to start a move on, for a pick's part of the day (DAY_WINDOWS' index). */
export const windowForPart = (part: VisitPart): number => (part === 'afternoon' ? 2 : 0);

/** What the card's chip says. */
export const requestChip = (r: Pick<VisitRequest, 'kind'>): { text: string; tone: string } =>
  r.kind === 'change'
    ? { text: 'Asked for another time', tone: 'warn' }
    : { text: 'Asked to cancel', tone: 'bad' };
